// src/components/proposal-formats/SectionTabSlider.tsx

import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

export type SectionTabOption<T extends string> = {
  id: T;
  label: string;
  /** Optional attention tone (amber) for tabs that need follow-up. */
  tone?: "default" | "warning";
  /** Optional badge rendered after the label (e.g. "!"). */
  badge?: string;
};

export type SectionTabSliderProps<T extends string> = {
  options: ReadonlyArray<SectionTabOption<T>>;
  value: T;
  onChange: (next: T) => void;
  ariaLabel?: string;
  /** Optional root anchor for tour highlights. */
  fireflyTargetId?: string;
  /** Per-tab anchors (`data-firefly-target` on each tab button). */
  tabTargetIds?: Partial<Record<T, string>>;
};

export function SectionTabSlider<T extends string>(props: SectionTabSliderProps<T>) {
  const { options, value, onChange, ariaLabel = "Section", fireflyTargetId, tabTargetIds } =
    props;
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      {...(fireflyTargetId ? { [FIREFLY_TARGET_ATTR]: fireflyTargetId } : {})}
      className="flex max-w-full flex-wrap gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1 text-xs dark:border-zinc-700 dark:bg-zinc-800/50"
    >
      {options.map((opt) => {
        const selected = value === opt.id;
        const warning = opt.tone === "warning";
        const selectedClass = warning
          ? "bg-amber-500 text-white"
          : "bg-indigo-600 text-white";
        const idleClass = warning
          ? "text-amber-800 hover:bg-amber-50 dark:text-amber-200 dark:hover:bg-amber-950/40"
          : "text-gray-700 hover:bg-white dark:text-zinc-200 dark:hover:bg-zinc-900";
        return (
          <button
            key={opt.id}
            type="button"
            role="tab"
            aria-selected={selected}
            {...(tabTargetIds?.[opt.id]
              ? { [FIREFLY_TARGET_ATTR]: tabTargetIds[opt.id] }
              : {})}
            onClick={() => onChange(opt.id)}
            className={
              "rounded-lg px-2.5 py-1.5 whitespace-nowrap transition-colors " +
              (selected ? selectedClass : idleClass)
            }
          >
            {opt.label}
            {opt.badge ? (
              <span
                className={
                  "ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold " +
                  (selected
                    ? "bg-white/25 text-white"
                    : "bg-amber-200 text-amber-900 dark:bg-amber-900/60 dark:text-amber-100")
                }
              >
                {opt.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
